import { useState, useEffect } from "react";
import {
  Loader2,
  Tags,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  Save,
} from "lucide-react";
import { colors, fonts, CONTACT_TYPES } from "../../styles/theme";
import { Button } from "../../components/ui";
import {
  fetchContactTypeMapping,
  updateContactTypeMapping,
} from "../../services/api";
import { inputStyle, DEFAULTS } from "./constants";

export default function ContactTypeMappingSection() {
  const [mapping, setMapping] = useState({});
  const [priority, setPriority] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newTagInputs, setNewTagInputs] = useState({});

  useEffect(() => {
    fetchContactTypeMapping()
      .then((data) => {
        setMapping(data.mapping || DEFAULTS.mapping);
        setPriority(data.priority || DEFAULTS.priority);
        setLastUpdated(
          data.updatedAt ? { at: data.updatedAt, by: data.updatedBy } : null,
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const tagsByType = {};
  for (const typeId of Object.keys(CONTACT_TYPES)) {
    tagsByType[typeId] = [];
  }
  for (const [tag, typeId] of Object.entries(mapping)) {
    if (!tagsByType[typeId]) tagsByType[typeId] = [];
    tagsByType[typeId].push(tag);
  }

  const removeTag = (tag) => {
    const updated = { ...mapping };
    delete updated[tag];
    setMapping(updated);
    setSuccess(null);
  };

  const addTag = (typeId) => {
    const tag = (newTagInputs[typeId] || "").trim().toLowerCase();
    if (!tag) return;
    setMapping({ ...mapping, [tag]: typeId });
    setNewTagInputs({ ...newTagInputs, [typeId]: "" });
    setSuccess(null);
  };

  const movePriority = (idx, direction) => {
    const newPri = [...priority];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newPri.length) return;
    [newPri[idx], newPri[swapIdx]] = [newPri[swapIdx], newPri[idx]];
    setPriority(newPri);
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const result = await updateContactTypeMapping(mapping, priority);
      setMapping(result.mapping);
      setPriority(result.priority);
      setLastUpdated({ at: result.updatedAt, by: result.updatedBy });
      setSuccess("Contact type mapping saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMapping({ ...DEFAULTS.mapping });
    setPriority([...DEFAULTS.priority]);
    setSuccess(null);
  };

  if (loading) {
    return (
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 24,
          textAlign: "center",
        }}
      >
        <Loader2
          size={18}
          color={colors.textSecondary}
          className="spin"
        />
      </div>
    );
  }

  const orderedTypes = [
    ...priority,
    ...Object.keys(CONTACT_TYPES).filter(
      (t) => !priority.includes(t) && t !== "subscriber",
    ),
  ];

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Tags size={18} color={colors.accent} />
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              margin: 0,
            }}
          >
            Contact Type Mapping
          </h3>
        </div>
        <Button
          variant="default"
          onClick={handleReset}
          style={{ fontSize: 11 }}
        >
          <RotateCcw size={12} /> Reset Defaults
        </Button>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Map GHL tags to contact types. When a contact has multiple matching
        tags, the highest-priority type wins. Use the arrows to reorder
        priority. The "Subscriber" type is the fallback for untagged contacts.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {orderedTypes.map((typeId, idx) => {
        const typeInfo = CONTACT_TYPES[typeId];
        if (!typeInfo) return null;
        const tags = tagsByType[typeId] || [];
        const priIdx = priority.indexOf(typeId);

        return (
          <div
            key={typeId}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              padding: 12,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: tags.length > 0 ? 8 : 0,
              }}
            >
              {priIdx >= 0 && (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 0 }}
                >
                  <ChevronUp
                    size={14}
                    color={priIdx > 0 ? colors.textSecondary : colors.border}
                    style={{ cursor: priIdx > 0 ? "pointer" : "default" }}
                    onClick={() => movePriority(priIdx, -1)}
                  />
                  <ChevronDown
                    size={14}
                    color={
                      priIdx < priority.length - 1
                        ? colors.textSecondary
                        : colors.border
                    }
                    style={{
                      cursor:
                        priIdx < priority.length - 1 ? "pointer" : "default",
                    }}
                    onClick={() => movePriority(priIdx, 1)}
                  />
                </div>
              )}
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 10px",
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: fonts.body,
                  color: typeInfo.color,
                  background: typeInfo.bg,
                }}
              >
                {typeInfo.label}
              </span>
              {priIdx >= 0 && (
                <span style={{ fontSize: 10, color: colors.textSecondary }}>
                  #{priIdx + 1}
                </span>
              )}
              <div style={{ flex: 1 }} />
              <input
                type="text"
                value={newTagInputs[typeId] || ""}
                onChange={(e) =>
                  setNewTagInputs({ ...newTagInputs, [typeId]: e.target.value })
                }
                onKeyDown={(e) => e.key === "Enter" && addTag(typeId)}
                placeholder="Add tag..."
                style={{
                  ...inputStyle,
                  width: 140,
                  padding: "4px 8px",
                  fontSize: 11,
                }}
              />
              <Plus
                size={14}
                color={colors.accent}
                style={{ cursor: "pointer", flexShrink: 0 }}
                onClick={() => addTag(typeId)}
              />
            </div>
            {tags.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 4,
                  paddingLeft: priIdx >= 0 ? 28 : 0,
                }}
              >
                {tags.sort().map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "2px 8px",
                      borderRadius: 10,
                      fontSize: 11,
                      fontFamily: fonts.body,
                      background: colors.bgHover,
                      color: colors.textSecondary,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    {tag}
                    <X
                      size={10}
                      color={colors.textSecondary}
                      style={{ cursor: "pointer" }}
                      onClick={() => removeTag(tag)}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: 6,
          padding: 12,
          marginBottom: 8,
          opacity: 0.7,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: fonts.body,
              color: CONTACT_TYPES.subscriber.color,
              background: CONTACT_TYPES.subscriber.bg,
            }}
          >
            {CONTACT_TYPES.subscriber.label}
          </span>
          <span
            style={{
              fontSize: 11,
              color: colors.textSecondary,
              fontStyle: "italic",
            }}
          >
            Default — contacts with no matching tags
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16,
        }}
      >
        {lastUpdated ? (
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Last saved {new Date(lastUpdated.at).toLocaleString()} by{" "}
            {lastUpdated.by}
          </span>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          {saving ? (
            <>
              <Loader2
                size={13}
                className="spin"
              />{" "}
              Saving...
            </>
          ) : (
            <>
              <Save size={13} /> Save Mapping
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
